import { dataLayerContext, dataLayerContextType, dataLayerPayload } from "@/contexts/context.datalayer";
import { optionContext, optionContextType } from "@/contexts/context.option";
import { productContext, productContextType } from "@/contexts/context.product";
import { Action, EventType, UpdateByDelta } from "@/events/events";
import { toDSLContext } from "@/renders/context";
import { consume, provide } from "@lit/context";
import { Task } from "@lit/task";
import { ProductBundleOption, ProductOption } from "@repobit/dex-store";
import { property } from "lit/decorators.js";
import { StateNode } from "./node.state";

export class OptionNode extends StateNode {
  @provide({ context: optionContext })
  @property({ attribute: false })
  _optionContext?: optionContextType;

  @consume({ context: dataLayerContext, subscribe: true })
  @property({ attribute: false })
  _dataLayer?: dataLayerContextType;

  @consume({ context: productContext, subscribe: true })
  @property({ attribute: false })
  product?: productContextType;

  @property({ attribute: false })
  bundle = [] as ProductBundleOption[];

  @property({ type: Number, reflect: true })
  devices?: number;

  @property({ type: Number, reflect: true })
  subscription?: number;

  // Optional data layer event name to publish when option loads/changes
  @property({ attribute: 'data-layer-event' })
  dataLayerEvent?: dataLayerPayload["event"];

  _option: ProductOption | null | undefined;
  // ensure datalayer is fired only once (first load)
  private _sentDataLayer = false;

  // Track attribute values last set by an incoming event
  // to avoid double work in _loadOptionByAttributes.
  private _pendingDevicesFromEvent     : number | undefined;
  private _pendingSubscriptionFromEvent: number | undefined;

  get option(): ProductOption | null | undefined {
    return this._option;
  }

  private _collectFromTask(taskComplete: Promise<ProductOption | null | undefined>) {
    this._optionContext = taskComplete;

    if (!this.noCollect) {
      this.collectOption({
        name   : this.storeName,
        options: taskComplete.then((option) => option ? [option] : [])
      });
    }
  }

  private _syncOptionAttributes(option: ProductOption) {
    const devices = option.getDevices();
    const subscription = option.getSubscription();

    // Mark as event-driven attribute update to avoid double work
    this._pendingDevicesFromEvent = devices;
    this._pendingSubscriptionFromEvent = subscription;

    this.devices = devices;
    this.subscription = subscription;
  }

  private _changeOptionEvent = new Task(this, {
    task: async ([evt, store]) => {
      if (!evt || !store) {
        return null;
      }

      // Ignore events matching this node's ignoreEvents list (works for DOM or context)
      if (this._isIgnoredSource(evt)) {
        return null;
      }

      this._collectFromTask(this._changeOptionEvent.taskComplete);

      let option: ProductOption | null | undefined = null;

      if (this.isActionEvent(evt)) {
        // Record event-driven attribute values (if any) so attribute task can skip
        this._pendingDevicesFromEvent = evt.detail?.devices;
        this._pendingSubscriptionFromEvent = evt.detail?.subscription;
        option = await this.__applyAction(evt.detail || {});
      }

      if (this.isBundleToogle(evt)) {
        option = await this.__applyBundle(evt.detail || {});
      }

      if (this.isDeltaUpdate(evt)) {
        option = await this._applyDeltaUpdate(evt.detail || {});
      }

      this._option = option;

      return option;
    },
    args: () => [this._event, this._store]
  });

  // Ensure datalayer is emitted if the callback becomes available slightly
  // after the first option was computed.
  private _fireDataLayerTask = new Task(this, {
    task: async ([opt, dl, evt]) => {
      if (this._sentDataLayer) return;
      if (opt && dl && evt) {
        dl({ option: opt, event: evt });
        this._sentDataLayer = true;
      }
    },
    args: () => [this._option, this._dataLayer, this.dataLayerEvent]
  });

  private _loadOptionByAttributes = new Task(this, {
    task: async ([devices, subscription]) => {
      const hasPendingDevices = this._pendingDevicesFromEvent !== undefined;
      const hasPendingSubscription = this._pendingSubscriptionFromEvent !== undefined;
      const matchesPendingDevices = !hasPendingDevices || devices === this._pendingDevicesFromEvent;
      const matchesPendingSubscription = !hasPendingSubscription || subscription === this._pendingSubscriptionFromEvent;

      // Skip only when current attribute values were just set by an event
      if (
        (hasPendingDevices || hasPendingSubscription)
        && matchesPendingDevices
        && matchesPendingSubscription
      ) {
        this._pendingDevicesFromEvent = undefined;
        this._pendingSubscriptionFromEvent = undefined;
        return this._option;
      }

      if (!this._option) {
        return null;
      }

      if (devices === undefined || subscription === undefined) {
        this._option = null;
        return null;
      }

      this._collectFromTask(this._loadOptionByAttributes.taskComplete);

      const option = await this.option?.getOption({ devices, subscription });

      this._option = option;
      return option;
    },
    args: () => [this.devices, this.subscription]
  });

  override getEtaArgs(): unknown[] {
    return [this._option, this.state, this.product, this._derived];
  }

  override async buildEtaContext(): Promise<object | undefined> {
    await this._changeOptionEvent.taskComplete;
    await this._loadOptionByAttributes.taskComplete;
    if (!this._option) return undefined;
    return await toDSLContext({ option: this._option, product: this.product, state: this.state, derived: this._derived });
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.noCollect) {
      this.collectOption({
        name   : this.storeName,
        options: Promise.resolve(this._option ? [this._option] : [])
      });
    }
  }

  disconnectedCallback() {
    this.collectOption({
      name   : this.storeName,
      options: null
    });
    super.disconnectedCallback();
  }

  protected async getUpdateComplete() {
    const result = await super.getUpdateComplete();
    await this._changeOptionEvent.taskComplete;
    await this._loadOptionByAttributes.taskComplete;
    await this._fireDataLayerTask.taskComplete;
    return result;
  }

  protected _eventChange(e: EventType): void {
    // Respect option-level ignore list
    if (this._isIgnoredSource(e)) {
      e.stopPropagation();
      return;
    }

    // Always capture locally
    this._eventDom = e;

    // Allow only product-change events to bubble up to <bd-product>
    if (this.isProductChange(e)) {
      return; // no stopPropagation → let it bubble
    }

    // Keep all other events scoped to this option
    e.stopPropagation();
  }

  private async _applyDeltaUpdate({
    type,
    delta,
    useAsValue,
    min = Number.MIN_SAFE_INTEGER,
    max = Number.MAX_SAFE_INTEGER
  }: UpdateByDelta) {
    const devices = useAsValue && type === "devices" ? Number(delta) : this.devices;
    const subscription = useAsValue && type === "subscription" ? Number(delta) : this.subscription;
    let option: ProductOption | undefined;

    if (useAsValue) {
      if (
        devices === undefined
        || subscription === undefined
        || Number.isNaN(devices)
        || Number.isNaN(subscription)
      ) {
        return null;
      }

      option = await this.option?.getOption({ devices, subscription });
    } else {
      option = await this.option?.nextOption(
        type === "devices" ?
          { devices: delta } :
          { subscription: delta }
      );
    }

    if (!option) {
      return null;
    }

    if (type === "devices" && (option.getDevices() < min || option.getDevices() > max)) {
      return null
    }

    if (type === "subscription" && (option.getSubscription() < min || option.getSubscription() > max)) {
      return null
    }

    this._syncOptionAttributes(option);
    return option;
  }

  private async __applyAction({ id, campaign, devices, subscription }: Action) {
    this.devices = devices ?? this.devices;
    this.subscription = subscription ?? this.subscription;

    if (this.devices === undefined || this.subscription === undefined) {
      return null;
    }

    if (id) {
      const product = await this._store?.getProduct({ id, campaign });
      return product?.getOption({
        devices     : this.devices,
        subscription: this.subscription,
        bundle      : this.option?.getBundle() || [] });
    }

    return this.option?.getOption({ devices: this.devices, subscription: this.subscription });
  }

  private async __applyBundle(option: Action) {
    const devices = option.devices ?? this.devices;
    const subscription = option.subscription ?? this.subscription;

    if (devices === undefined || subscription === undefined || !option.id) {
      return null
    }

    const productToBeBundled = await this._store?.getProduct({ id: option.id, campaign: option.campaign })

    const optionToBeBundled = await productToBeBundled?.getOption({ devices, subscription })

    if (!optionToBeBundled) {
      return null;
    }

    if (this.devices !== undefined && this.subscription !== undefined) {
      return this.option?.toogleBundle({
        devicesFixed     : Boolean(option.devices),
        subscriptionFixed: Boolean(option.subscription),
        option           : optionToBeBundled
      });
    }
    return null;
  }

}
